import React, { useState, useRef, useEffect, useCallback } from 'react'
import { Plus, X, Filter, Pencil } from 'lucide-react'
import styles from './AdvancedSearch.module.scss'
import { config } from '../../config'

// All fields from daily-feed-metrics index mapping
export const ALL_FIELDS = [
  // Core identification
  { value: 'correlation_id', label: 'Correlation ID', type: 'keyword' },

  // Vehicle core attributes
  { value: 'vin', label: 'VIN', type: 'keyword' },
  { value: 'stock', label: 'Stock', type: 'keyword' },
  { value: 'year', label: 'Year', type: 'integer' },
  { value: 'type', label: 'Type', type: 'keyword' },
  { value: 'miles', label: 'Miles', type: 'integer' },
  { value: 'price', label: 'Price', type: 'float' },
  { value: 'certified', label: 'Certified', type: 'boolean' },

  // Processing status
  { value: 'db_record_id', label: 'DB Record ID', type: 'long' },
  { value: 'db_check_status', label: 'DB Check Status', type: 'keyword' },
  { value: 'http_legacy_status', label: 'HTTP Legacy Status', type: 'keyword' },
  { value: 'http_legacy_error_type', label: 'HTTP Legacy Error Type', type: 'keyword' },
  { value: 'http_legacy_message', label: 'HTTP Legacy Error Message', type: 'text' },

  // Provider info
  { value: 'code', label: 'Provider Code', type: 'keyword' },

  // File paths
  { value: 'file_path', label: 'Origin File Path', type: 'keyword' },
  { value: 'batch_file_path', label: 'Batch File Path', type: 'keyword' },

  // Dealer relations
  { value: 'relations.dealer.id', label: 'Dealer ID', type: 'keyword' },
  { value: 'relations.dealer.name', label: 'Dealer Name', type: 'keyword' },
  { value: 'relations.dealer.db_record_id', label: 'Dealer DB Record ID', type: 'long' },
  { value: 'relations.dealer.db_check_status', label: 'Dealer DB Check Status', type: 'keyword' },
  { value: 'relations.dealer.http_legacy_status', label: 'Dealer HTTP Legacy Status', type: 'keyword' },
  { value: 'relations.dealer.http_legacy_error_type', label: 'Dealer HTTP Legacy Error', type: 'keyword' },
  { value: 'relations.dealer.batch_file_path', label: 'Dealer Batch File', type: 'keyword' },

  // Model relations
  { value: 'relations.model.id', label: 'Model ID', type: 'keyword' },
  { value: 'relations.model.make', label: 'Make', type: 'keyword' },
  { value: 'relations.model.title', label: 'Model Title', type: 'keyword' },
  { value: 'relations.model.db_record_id', label: 'Model DB Record ID', type: 'long' },
  { value: 'relations.model.db_check_status', label: 'Model DB Check Status', type: 'keyword' },
  { value: 'relations.model.http_legacy_status', label: 'Model HTTP Legacy Status', type: 'keyword' },
  { value: 'relations.model.http_legacy_error_type', label: 'Model HTTP Legacy Error', type: 'keyword' },
  { value: 'relations.model.http_legacy_message', label: 'Model Error Message', type: 'text' },
  { value: 'relations.model.batch_file_path', label: 'Model Batch File', type: 'keyword' },

  // Modification relations
  { value: 'relations.modification.id', label: 'Modification ID', type: 'keyword' },
  { value: 'relations.modification.transmission', label: 'Transmission', type: 'keyword' },
  { value: 'relations.modification.db_record_id', label: 'Modification DB Record ID', type: 'long' },
  { value: 'relations.modification.db_check_status', label: 'Modification DB Check Status', type: 'keyword' },
  { value: 'relations.modification.http_legacy_status', label: 'Modification HTTP Legacy Status', type: 'keyword' },
  { value: 'relations.modification.http_legacy_error_type', label: 'Modification HTTP Legacy Error', type: 'keyword' },
  { value: 'relations.modification.http_legacy_message', label: 'Modification Error Message', type: 'text' },
  { value: 'relations.modification.batch_file_path', label: 'Modification Batch File', type: 'keyword' },

  // Generation relations
  { value: 'relations.generation.id', label: 'Generation ID', type: 'keyword' },
  { value: 'relations.generation.title', label: 'Generation Title', type: 'keyword' },
  { value: 'relations.generation.db_record_id', label: 'Generation DB Record ID', type: 'long' },
  { value: 'relations.generation.db_check_status', label: 'Generation DB Check Status', type: 'keyword' },
  { value: 'relations.generation.http_legacy_status', label: 'Generation HTTP Legacy Status', type: 'keyword' },
  { value: 'relations.generation.http_legacy_error_type', label: 'Generation HTTP Legacy Error', type: 'keyword' },
  { value: 'relations.generation.http_legacy_message', label: 'Generation Error Message', type: 'text' },
  { value: 'relations.generation.batch_file_path', label: 'Generation Batch File', type: 'keyword' },
]

const OPERATORS = [
  { value: 'equals', label: '=' },
  { value: 'not_equals', label: '!=' },
  { value: 'greater_than', label: '>' },
  { value: 'less_than', label: '<' },
  { value: 'contains', label: 'contains' },
  { value: 'starts_with', label: 'starts with' },
  { value: 'ends_with', label: 'ends with' },
  { value: 'exists', label: 'exists' },
  { value: 'not_exists', label: 'not exists' },
]

export interface Filter {
  field: string
  operator: string
  value: string
}

interface AdvancedSearchProps {
  onSearch: (filters: Filter[]) => void
  datePattern?: string
  filters?: Filter[]
}

const FILTERS_STORAGE_KEY = 'insights_search_filters'

// Debounce hook
function useDebounce<T>(value: T, delay: number): T {
  const [debouncedValue, setDebouncedValue] = useState<T>(value)

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value)
    }, delay)

    return () => {
      clearTimeout(handler)
    }
  }, [value, delay])

  return debouncedValue
}

const AdvancedSearch: React.FC<AdvancedSearchProps> = ({ onSearch, datePattern, filters: externalFilters }) => {
  // Initialize filters from localStorage or external prop
  const [filters, setFilters] = useState<Filter[]>(() => {
    try {
      const saved = localStorage.getItem(FILTERS_STORAGE_KEY)
      if (saved) {
        const parsed = JSON.parse(saved)
        if (Array.isArray(parsed)) return parsed
      }
    } catch (e) {
      console.error('Failed to load filters from localStorage', e)
    }
    return externalFilters || []
  })
  const [fieldQuery, setFieldQuery] = useState('')
  const [currentField, setCurrentField] = useState(ALL_FIELDS[0].value)
  const [currentOperator, setCurrentOperator] = useState('equals')
  const [currentValue, setCurrentValue] = useState('')
  const [showFieldDropdown, setShowFieldDropdown] = useState(false)
  const [showValueDropdown, setShowValueDropdown] = useState(false)
  const [highlightedIndex, setHighlightedIndex] = useState(0)
  const [highlightedValueIndex, setHighlightedValueIndex] = useState(0)
  const [valueSuggestions, setValueSuggestions] = useState<string[]>([])
  const [loadingSuggestions, setLoadingSuggestions] = useState(false)

  const fieldInputRef = useRef<HTMLInputElement>(null)
  const valueInputRef = useRef<HTMLInputElement>(null)
  const dropdownRef = useRef<HTMLDivElement>(null)
  const valueDropdownRef = useRef<HTMLDivElement>(null)

  // Debounce the value input for autocomplete
  const debouncedValue = useDebounce(currentValue, 300)

  // Get current field config
  const currentFieldConfig = ALL_FIELDS.find((f) => f.value === currentField)
  const isKeywordField = currentFieldConfig?.type === 'keyword'

  const filteredFields = ALL_FIELDS.filter(
    (f) =>
      f.value.toLowerCase().includes(fieldQuery.toLowerCase()) ||
      f.label.toLowerCase().includes(fieldQuery.toLowerCase()),
  )

  // Fetch value suggestions for keyword fields
  const fetchValueSuggestions = useCallback(async () => {
    if (!isKeywordField) {
      setValueSuggestions([])
      return
    }

    setLoadingSuggestions(true)
    try {
      const params = new URLSearchParams()
      if (datePattern) params.append('date', datePattern)
      params.append('field', currentField)
      // Only include query if there's a value to filter by
      if (debouncedValue && debouncedValue.length > 0) {
        params.append('query', debouncedValue)
      }

      const res = await fetch(`${config.MONITOR_API_URL}/insights/field-values?${params.toString()}`)
      const data = await res.json()
      setValueSuggestions(Array.isArray(data) ? data : [])
    } catch (e) {
      console.error('Failed to fetch suggestions', e)
      setValueSuggestions([])
    } finally {
      setLoadingSuggestions(false)
    }
  }, [currentField, debouncedValue, datePattern, isKeywordField])

  // Fetch suggestions when field changes or debounced value updates
  useEffect(() => {
    if (isKeywordField) {
      fetchValueSuggestions()
    }
  }, [fetchValueSuggestions, isKeywordField])

  // Sync filters to localStorage whenever they change
  useEffect(() => {
    try {
      localStorage.setItem(FILTERS_STORAGE_KEY, JSON.stringify(filters))
    } catch (e) {
      console.error('Failed to save filters to localStorage', e)
    }
  }, [filters])

  // Sync with external filters prop if provided
  useEffect(() => {
    if (externalFilters && JSON.stringify(externalFilters) !== JSON.stringify(filters)) {
      setFilters(externalFilters)
    }
  }, [externalFilters])

  // Call onSearch on initial mount with saved filters
  useEffect(() => {
    if (filters.length > 0) {
      onSearch(filters)
    }
  }, []) // Only on mount

  useEffect(() => {
    setHighlightedIndex(0)
  }, [fieldQuery])

  useEffect(() => {
    setHighlightedValueIndex(0)
  }, [valueSuggestions])

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setShowFieldDropdown(false)
      }
      if (valueDropdownRef.current && !valueDropdownRef.current.contains(e.target as Node)) {
        setShowValueDropdown(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  // Reset value when field changes
  useEffect(() => {
    setCurrentValue('')
    setValueSuggestions([])
  }, [currentField])

  const selectField = (fieldValue: string) => {
    setCurrentField(fieldValue)
    const field = ALL_FIELDS.find((f) => f.value === fieldValue)
    setFieldQuery(field?.label || fieldValue)
    setShowFieldDropdown(false)
  }

  const selectValue = (value: string) => {
    setCurrentValue(value)
    setShowValueDropdown(false)
  }

  const handleFieldKeyDown = (e: React.KeyboardEvent) => {
    if (!showFieldDropdown) return

    if (e.key === 'ArrowDown') {
      e.preventDefault()
      setHighlightedIndex((prev) => Math.min(prev + 1, filteredFields.length - 1))
    } else if (e.key === 'ArrowUp') {
      e.preventDefault()
      setHighlightedIndex((prev) => Math.max(prev - 1, 0))
    } else if (e.key === 'Enter') {
      e.preventDefault()
      if (filteredFields.length > 0) {
        selectField(filteredFields[highlightedIndex].value)
      }
    } else if (e.key === 'Escape') {
      setShowFieldDropdown(false)
    }
  }

  const handleValueKeyDown = (e: React.KeyboardEvent) => {
    if (showValueDropdown && valueSuggestions.length > 0) {
      if (e.key === 'ArrowDown') {
        e.preventDefault()
        setHighlightedValueIndex((prev) => Math.min(prev + 1, valueSuggestions.length - 1))
      } else if (e.key === 'ArrowUp') {
        e.preventDefault()
        setHighlightedValueIndex((prev) => Math.max(prev - 1, 0))
      } else if (e.key === 'Enter') {
        e.preventDefault()
        selectValue(valueSuggestions[highlightedValueIndex])
        return
      } else if (e.key === 'Escape') {
        setShowValueDropdown(false)
        return
      }
    }

    if (e.key === 'Enter' && !showValueDropdown) {
      e.preventDefault()
      addFilter()
    }
  }

  const addFilter = () => {
    if (!currentValue.trim() && currentOperator !== 'exists' && currentOperator !== 'not_exists') {
      return
    }

    const newFilter = {
      field: currentField,
      operator: currentOperator,
      value: currentValue.trim(),
    }

    const updatedFilters = [...filters, newFilter]
    setFilters(updatedFilters)
    setCurrentValue('')
    setValueSuggestions([])
    onSearch(updatedFilters)
  }

  const removeFilter = (index: number) => {
    const updatedFilters = filters.filter((_, i) => i !== index)
    setFilters(updatedFilters)
    onSearch(updatedFilters)
  }

  const editFilter = (index: number) => {
    const filter = filters[index]

    // Set the filter values in the builder
    setCurrentField(filter.field)
    setCurrentOperator(filter.operator)
    setCurrentValue(filter.value)

    // Update field query to show the label
    const fieldConfig = ALL_FIELDS.find((f) => f.value === filter.field)
    setFieldQuery(fieldConfig?.label || filter.field)

    // Remove the filter from the list
    const updatedFilters = filters.filter((_, i) => i !== index)
    setFilters(updatedFilters)
    onSearch(updatedFilters)

    // Focus on value input after a short delay
    setTimeout(() => {
      valueInputRef.current?.focus()
      valueInputRef.current?.select()
    }, 100)
  }

  const clearAllFilters = () => {
    setFilters([])
    onSearch([])
  }

  const needsValue = currentOperator !== 'exists' && currentOperator !== 'not_exists'

  const getFieldLabel = (fieldValue: string) => {
    return ALL_FIELDS.find((f) => f.value === fieldValue)?.label || fieldValue
  }

  const getOperatorLabel = (opValue: string) => {
    return OPERATORS.find((o) => o.value === opValue)?.label || opValue
  }

  return (
    <div className={styles.container}>
      <div className={styles.builderRow}>
        <div className={styles.autocompleteWrapper} ref={dropdownRef}>
          <input
            ref={fieldInputRef}
            type="text"
            value={fieldQuery}
            onChange={(e) => {
              setFieldQuery(e.target.value)
              setShowFieldDropdown(true)
            }}
            onFocus={() => setShowFieldDropdown(true)}
            onKeyDown={handleFieldKeyDown}
            placeholder="Select field..."
            className={styles.fieldInput}
          />
          {showFieldDropdown && filteredFields.length > 0 && (
            <div className={styles.dropdown}>
              {filteredFields.slice(0, 15).map((field, idx) => (
                <div
                  key={field.value}
                  className={`${styles.dropdownItem} ${idx === highlightedIndex ? styles.highlighted : ''}`}
                  onClick={() => selectField(field.value)}
                  onMouseEnter={() => setHighlightedIndex(idx)}>
                  <span className={styles.fieldLabel}>{field.label}</span>
                  <span className={styles.fieldPath}>{field.value}</span>
                </div>
              ))}
            </div>
          )}
        </div>

        <select
          value={currentOperator}
          onChange={(e) => setCurrentOperator(e.target.value)}
          className={styles.operatorSelect}>
          {OPERATORS.map((op) => (
            <option key={op.value} value={op.value}>
              {op.label}
            </option>
          ))}
        </select>

        {needsValue && (
          <div className={styles.valueWrapper} ref={valueDropdownRef}>
            <input
              ref={valueInputRef}
              type="text"
              value={currentValue}
              onChange={(e) => {
                setCurrentValue(e.target.value)
                if (isKeywordField) {
                  setShowValueDropdown(true)
                }
              }}
              onFocus={() => {
                if (isKeywordField) {
                  setShowValueDropdown(true)
                }
              }}
              onKeyDown={handleValueKeyDown}
              placeholder={isKeywordField ? 'Select or type...' : 'Enter value...'}
              className={styles.valueInput}
            />
            {isKeywordField && showValueDropdown && (valueSuggestions.length > 0 || loadingSuggestions) && (
              <div className={styles.valueDropdown}>
                {loadingSuggestions ? (
                  <div className={styles.loadingItem}>Loading...</div>
                ) : (
                  valueSuggestions.map((suggestion, idx) => (
                    <div
                      key={suggestion}
                      className={`${styles.valueItem} ${idx === highlightedValueIndex ? styles.highlighted : ''}`}
                      onClick={() => selectValue(suggestion)}
                      onMouseEnter={() => setHighlightedValueIndex(idx)}>
                      {suggestion}
                    </div>
                  ))
                )}
              </div>
            )}
          </div>
        )}

        <button onClick={addFilter} className={styles.addBtn} disabled={needsValue && !currentValue.trim()}>
          <Plus size={16} />
          Add
        </button>
      </div>

      {filters.length > 0 && (
        <div className={styles.activeFilters}>
          <div className={styles.filterLabel}>
            <Filter size={14} />
            Active Filters:
          </div>
          <div className={styles.filterTags}>
            {filters.map((filter, idx) => (
              <div key={idx} className={styles.filterTag}>
                <span className={styles.tagField}>{getFieldLabel(filter.field)}</span>
                <span className={styles.tagOperator}>{getOperatorLabel(filter.operator)}</span>
                {filter.value && <span className={styles.tagValue}>{filter.value}</span>}
                <button onClick={() => editFilter(idx)} className={styles.tagEdit} title="Edit filter">
                  <Pencil size={12} />
                </button>
                <button onClick={() => removeFilter(idx)} className={styles.tagRemove} title="Remove filter">
                  <X size={12} />
                </button>
              </div>
            ))}
          </div>
          <button onClick={clearAllFilters} className={styles.clearAllBtn}>
            Clear All
          </button>
        </div>
      )}
    </div>
  )
}

export default AdvancedSearch
